import { useCallback } from 'preact/hooks';

import { usePlayer } from './PlayerProvider';

const DEFAULT_DVR_SEEK_LIMIT = 25;

export const getDvrSeekLimit = (player: jwplayer.JWPlayer | null) => {
  return player?.getConfig().dvrSeekLimit ?? DEFAULT_DVR_SEEK_LIMIT;
};

export const useSeek = () => {
  const player = usePlayer();

  const seekBy = useCallback(
    (amount: number) => {
      const jw = player.getJWPlayer();
      const position = jw?.getPosition() ?? 0;

      let min = 0;
      let max = jw?.getDuration();

      if (max < 0) {
        const dvrSeekLimit = getDvrSeekLimit(jw);

        min = max;
        max = Math.max(position, -dvrSeekLimit);
      }

      const targetPosition = Math.min(Math.max(position + amount, min), max);

      player.seek(targetPosition);
    },
    [player]
  );

  const rewindBy = useCallback(
    (amount: number) => {
      seekBy(-amount);
    },
    [seekBy]
  );

  const seekTo = useCallback(
    (percentage: number) => {
      const jw = player.getJWPlayer();
      const duration = jw?.getDuration() ?? 0;

      if (duration >= 0) {
        player.seek((percentage / 100) * duration);
      } else {
        const dvrPosition = Math.min(jw?.getPosition() ?? 0, -1);
        const dvrSeekLimit = getDvrSeekLimit(jw);

        const position = duration - (percentage / 100) * duration;

        player.seek(Math.min(position, Math.max(dvrPosition, -dvrSeekLimit)));
      }
    },
    [player]
  );

  const seekToLive = useCallback(() => {
    seekTo(100);
  }, [seekTo]);

  const seekToBeginning = useCallback(() => {
    seekTo(0);
  }, [seekTo]);

  return {
    seekBy,
    seekToLive,
    fastForwardBy: seekBy,
    rewindBy,
    seekTo,
    seekToBeginning,
  };
};
